import { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Check, X, Plus, Dumbbell, Trophy, Clock, Replace, Search, Coffee, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { useBackHandler } from '../../lib/backNav';

const fmtLast = (last) =>
  last.sets.map((s) => `${Number(s.weight)}×${s.reps}`).join('   ');

// RPE-aware weight suggestion: estimate 1RM from last session (Epley on
// reps + reps-in-reserve, RIR = 10 - RPE), then back-calc a weight to hit the
// target reps at ~RPE 8 (RIR 2). Rounds to the nearest 5 lb. null if no usable
// history (e.g. bodyweight-only).
const suggestWeight = (last, targetReps) => {
  if (!last || !last.sets || !last.sets.length) return null;
  let e1rm = 0;
  for (const s of last.sets) {
    const w = Number(s.weight);
    if (!w || s.reps == null) continue;
    const rir = s.rpe != null ? Math.max(0, 10 - Number(s.rpe)) : 0;
    const est = w * (1 + (Number(s.reps) + rir) / 30);
    if (est > e1rm) e1rm = est;
  }
  if (!e1rm) return null;
  const reps = targetReps || 8;
  const raw = e1rm / (1 + (reps + 2) / 30);
  return Math.max(5, Math.round(raw / 5) * 5);
};

const lastTopWeight = (last) => {
  const ws = (last?.sets || []).map((s) => Number(s.weight)).filter((w) => w);
  return ws.length ? Math.max(...ws) : null;
};

// Prefill set i's weight from the last session's matching set (set 1 → last set 1,
// etc.). Sets beyond what was logged last time inherit the last logged set's
// weight. No history → fall back to the exercise's suggested/program weight.
const prefillWeight = (ex, i) => {
  const ls = ex.last && ex.last.sets;
  if (ls && ls.length) {
    const s = ls[i] || ls[ls.length - 1];
    if (s && s.weight != null) return String(Number(s.weight));
  }
  const sug = Number(ex.suggested);
  return Number.isFinite(sug) ? String(sug) : '0'; // no data → 0 (not "null")
};

const todayStr = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
};

const fmtDur = (sec) => {
  sec = Math.max(0, Math.floor(sec || 0));
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s = sec % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    : `${m}:${String(s).padStart(2, '0')}`;
};

// Group consecutive exercises that share a block (superset/circuit) for display.
const groupByBlock = (workout) => {
  const groups = [];
  for (const ex of workout) {
    const last = groups[groups.length - 1];
    if (last && ex.blockId != null && last.blockId === ex.blockId) last.exercises.push(ex);
    else groups.push({ blockId: ex.blockId, type: ex.blockType || 'single', rounds: ex.blockRounds, exercises: [ex] });
  }
  return groups;
};

// Player navigation "stops". A SINGLE-type block holds straight sets — its
// exercises are done one at a time, so each becomes its own stop. Only
// superset/circuit blocks (interleaved by round) stay grouped into one stop.
// (A single block can legitimately contain many exercises — that's a normal
// straight-set day, NOT a superset.)
const buildStops = (workout) => {
  const stops = [];
  for (const g of groupByBlock(workout)) {
    if (g.type === 'superset' || g.type === 'circuit') stops.push(g);
    else for (const ex of g.exercises) stops.push({ blockId: g.blockId, type: 'single', rounds: null, exercises: [ex] });
  }
  return stops;
};

const WorkoutView = ({ api, token, date, onExit, onImmersiveChange }) => {
  const targetDate = date || todayStr(); // which scheduled day to run (calendar flex-start passes a date; null = today)
  const [workout, setWorkout] = useState(null);   // array of exercises w/ meta
  const [scheduled, setScheduled] = useState(null); // { week, day, focus } | null (rest day)
  const [phase, setPhase] = useState('start');    // 'start' | 'playing' | 'done'
  const [sessionId, setSessionId] = useState(null);
  const [idx, setIdx] = useState(0);
  const [sets, setSets] = useState({});           // { [exerciseId]: [{weight,reps,rpe,saved}] }
  const [notes, setNotes] = useState({});         // { [exerciseId]: string }
  const [noteSaved, setNoteSaved] = useState({}); // { [exerciseId]: bool }
  const [summary, setSummary] = useState(null);
  const [newPRs, setNewPRs] = useState([]);       // PRs set this session (celebrated on summary)
  const [loading, setLoading] = useState(true);
  const [completedToday, setCompletedToday] = useState(null); // today's completed session stats (block re-start)
  const [resumeId, setResumeId] = useState(null); // in-progress session to resume
  const [resumeStart, setResumeStart] = useState(null); // resumed session's started_at (ms)
  const [startedAt, setStartedAt] = useState(null); // active session start (ms epoch)
  const [now, setNow] = useState(Date.now());
  const [subOpen, setSubOpen] = useState(null);   // exercise being substituted (picker target), or null
  const [addOpen, setAddOpen] = useState(false);  // add-an-exercise picker
  const [confirmCancel, setConfirmCancel] = useState(false); // discard-workout confirm
  const [discardError, setDiscardError] = useState(null);
  const [exVideo, setExVideo] = useState(null);   // superset exercise video open in modal
  const [rirQueue, setRirQueue] = useState([]);     // exercises pending a "reps in the tank" question [{id,name}]
  const [rirAsked, setRirAsked] = useState({});     // exercise id -> already asked this session
  const pendingNavRef = useRef(null);               // 'next' | 'finish' to run once the RIR queue drains
  const [warmups, setWarmups] = useState([]);     // saved warmups to pick from
  const [warmupId, setWarmupId] = useState('');   // selected warmup
  const [warmupDetail, setWarmupDetail] = useState(null); // its moves
  const [warmupVideo, setWarmupVideo] = useState(null);   // video id open in modal
  const [assessment, setAssessment] = useState(null);     // AI coach feedback (done screen)
  const [assessLoading, setAssessLoading] = useState(false);

  // On the summary screen, fetch the AI coach assessment for the finished session.
  useEffect(() => {
    if (phase !== 'done' || !sessionId) return;
    setAssessLoading(true);
    api.post(`/sessions/${sessionId}/assessment`, {}, token)
      .then((r) => setAssessment(r.assessment))
      .catch(() => setAssessment(null))
      .finally(() => setAssessLoading(false));
  }, [phase, sessionId, api, token]);

  // Advancing to a new block should start at the top of its card, not wherever
  // the previous (often longer) card left the page scrolled.
  useEffect(() => { window.scrollTo(0, 0); }, [idx]);

  // Saved warmups for the start screen picker.
  useEffect(() => { api.get('/warmups', token).then(setWarmups).catch(() => setWarmups([])); }, [api, token]);
  useEffect(() => {
    if (!warmupId) { setWarmupDetail(null); return; }
    api.get(`/warmups/${warmupId}`, token).then(setWarmupDetail).catch(() => setWarmupDetail(null));
  }, [api, token, warmupId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get(`/workout?date=${targetDate}`, token);
        const sched = data.scheduled || null;
        const roundsByBlock = {};
        for (const b of data.blocks || []) roundsByBlock[b.id] = b.rounds;
        const w = (data.exercises || []).map((pe) => ({
          id: pe.id,
          name: pe.name,
          youtube_video_id: pe.youtube_video_id,
          muscle_group: pe.muscle_group,
          targetSets: pe.target_sets,
          targetReps: pe.reps_default,
          targetRepsText: pe.reps_text,
          suggested: pe.suggested_weight,
          suggestedText: pe.suggested_text,
          coach: pe.coach_note,
          blockId: pe.block_id,
          blockType: pe.block_type || 'single',
          blockRounds: roundsByBlock[pe.block_id] || null,
        }));
        if (w.length) {
          const ids = w.map((e) => e.id).join(',');
          const td = await api.get(`/exercises/training-data?ids=${ids}`, token).catch(() => ({}));
          for (const ex of w) {
            const d = td[ex.id] || {};
            ex.last = d.last || null;          // { date, sets, e1rm } | null
            ex.prs = d.prs || null;            // { e1rm, rep10, max_weight, session_volume }
            ex.noteInit = d.note || '';
            const computed = suggestWeight(ex.last, ex.targetReps);
            if (computed != null) {
              const top = lastTopWeight(ex.last);
              const delta = top != null ? computed - top : 0;
              ex.suggested = computed;
              ex.suggestedText = `${computed} lb`;
              ex.suggestedFromHistory = true;
              ex.suggestedDelta = delta; // +/- vs last session's top set
            }
          }
        }
        // Seed set rows. In a superset/circuit, every exercise gets ONE set per
        // round (block rounds), so the player can interleave A→B→A→B by round.
        const initSets = {}, initNotes = {};
        for (const g of buildStops(w)) {
          const multi = g.exercises.length > 1;
          const count = multi ? (g.rounds || Math.max(...g.exercises.map((e) => e.targetSets))) : null;
          for (const ex of g.exercises) {
            const n = count || ex.targetSets;
            initSets[ex.id] = Array.from({ length: n }, (_, idx) => ({
              weight: prefillWeight(ex, idx), reps: String(ex.targetReps), rpe: '', saved: false, touched: false,
            }));
            initNotes[ex.id] = ex.noteInit;
          }
        }

        // Resume an in-progress session for today (if any): overlay its logged sets.
        const active = await api.get(`/sessions/active?date=${targetDate}`, token).catch(() => null);
        if (active?.session) {
          const byEx = {};
          for (const s of active.session.sets) (byEx[s.exercise_id] ||= []).push(s);
          for (const ex of w) {
            for (const ls of byEx[ex.id] || []) {
              const i = ls.set_number - 1;
              while (initSets[ex.id].length <= i) {
                initSets[ex.id].push({ weight: prefillWeight(ex, initSets[ex.id].length), reps: String(ex.targetReps), rpe: '', saved: false, touched: false });
              }
              initSets[ex.id][i] = {
                weight: ls.weight != null ? String(Number(ls.weight)) : '',
                reps: ls.reps != null ? String(ls.reps) : '',
                rpe: ls.rpe != null ? String(Number(ls.rpe)) : '',
                saved: true, touched: true,
              };
            }
          }
          if (!cancelled) {
            setResumeId(active.session.id);
            setResumeStart(Date.parse(active.session.started_at));
          }
        }

        if (cancelled) return;
        setScheduled(sched);
        setCompletedToday(data.completed || null);
        setWorkout(w);
        setSets(initSets);
        setNotes(initNotes);
      } catch {
        if (!cancelled) { setScheduled(null); setWorkout([]); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [api, token, targetDate]);

  // The player phase goes full-screen — tell the shell to hide its chrome.
  useEffect(() => {
    onImmersiveChange?.(phase === 'playing');
    return () => onImmersiveChange?.(false);
  }, [phase, onImmersiveChange]);

  // Scroll back to the top when moving between exercises / phases.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [idx, phase]);

  // Tick the workout timer while playing (elapsed is derived from started_at).
  useEffect(() => {
    if (phase !== 'playing' || !startedAt) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [phase, startedAt]);

  // Back-gesture / system-back handling: close the top-most overlay first, else
  // exit the running player.
  useBackHandler(confirmCancel, () => setConfirmCancel(false));
  useBackHandler(rirQueue.length > 0, () => {
    setRirAsked((p) => { const n = { ...p }; rirQueue.forEach((e) => { n[e.id] = true; }); return n; });
    setRirQueue([]); pendingNavRef.current = null;
  });
  useBackHandler(!!exVideo, () => setExVideo(null));
  useBackHandler(!!warmupVideo, () => setWarmupVideo(null));
  useBackHandler(phase === 'playing' || phase === 'done', onExit);

  const elapsed = startedAt ? (now - startedAt) / 1000 : 0;

  const workoutTitle = scheduled
    ? `Week ${scheduled.week} · Day ${scheduled.day} · ${scheduled.focus}`
    : 'Workout';

  // Resume at the first block that isn't fully logged (idx is block-based).
  const firstIncompleteIdx = () => {
    const bl = buildStops(workout);
    for (let i = 0; i < bl.length; i++) {
      const multi = bl[i].exercises.length > 1;
      const target = multi ? (bl[i].rounds || Math.max(...bl[i].exercises.map((e) => e.targetSets))) : null;
      for (const ex of bl[i].exercises) {
        const need = target || ex.targetSets;
        const saved = (sets[ex.id] || []).filter((r) => r.saved).length;
        if (saved < need) return i;
      }
    }
    return 0;
  };

  const startWorkout = async () => {
    let sid = resumeId;
    let start = resumeStart;
    if (!sid) {
      const s = await api.post('/sessions', { label: workoutTitle, date: targetDate }, token);
      sid = s.id;
      start = Date.parse(s.started_at);
    }
    setSessionId(sid);
    setStartedAt(start);
    setNow(Date.now());
    setIdx(resumeId ? firstIncompleteIdx() : 0);
    setPhase('playing');
  };

  const saveTimers = useRef({});

  const hasData = (row) => String(row.reps) !== '' || String(row.weight) !== '';

  const postSet = (exId, i, row) =>
    api.post(`/sessions/${sessionId}/sets`, {
      exercise_id: exId, set_number: i + 1, weight: row.weight, reps: row.reps, rpe: row.rpe,
    }, token);

  // Save one set; marks it logged. Called on wheel change, blur, or the ✓ tap.
  const persistSet = async (exId, i, row) => {
    if (!sessionId || !hasData(row)) return;
    try {
      await postSet(exId, i, row);
      setSets((prev) => ({
        ...prev,
        [exId]: prev[exId].map((r, ri) => (ri === i ? { ...r, saved: true, touched: true } : r)),
      }));
    } catch { /* keep as unsaved */ }
  };

  // The ✓ tap just logs the set. The "reps in the tank" question is asked when
  // you LEAVE an exercise (Next/Finish), so it fires reliably even when sets are
  // auto-saved by tapping out of a field rather than tapping ✓.
  const logSet = (ex, i, row) => persistSet(ex.id, i, row);

  // Exercises in a block that the user logged work on and hasn't been asked about.
  const exercisesNeedingRir = (block) =>
    block ? block.exercises.filter((ex) => !rirAsked[ex.id] && (sets[ex.id] || []).some((r) => r.touched && hasData(r))) : [];

  // Drain the queue; once empty, run the pending Next/Finish that triggered it.
  const drainRir = (rest) => {
    setRirQueue(rest);
    if (!rest.length) {
      const nav = pendingNavRef.current;
      pendingNavRef.current = null;
      if (nav === 'next') setIdx(idx + 1);
      else if (nav === 'finish') finish();
    }
  };

  // Answer: store reps-in-reserve as RPE (= 10 - RIR) on the exercise's last set
  // so the suggestion engine keeps working. The word "RPE" is never shown.
  const answerRir = (rir) => {
    const cur = rirQueue[0];
    if (!cur) return;
    const exId = cur.id;
    const rpe = String(Math.max(0, Math.min(10, 10 - rir)));
    setSets((prev) => {
      const r = prev[exId] || [];
      const li = r.length - 1;
      if (li < 0) return prev;
      const updated = { ...r[li], rpe, touched: true };
      if (sessionId) postSet(exId, li, updated).catch(() => {});
      return { ...prev, [exId]: r.map((x, xi) => (xi === li ? updated : x)) };
    });
    setRirAsked((p) => ({ ...p, [exId]: true }));
    drainRir(rirQueue.slice(1));
  };

  const skipRir = () => {
    const cur = rirQueue[0];
    if (cur) setRirAsked((p) => ({ ...p, [cur.id]: true }));
    drainRir(rirQueue.slice(1));
  };

  // Next / Finish: ask the reps-in-the-tank question for any logged-but-unasked
  // exercise in the current block first, then advance/finish.
  const handleNext = (block) => {
    const pend = exercisesNeedingRir(block);
    if (pend.length) { pendingNavRef.current = 'next'; setRirQueue(pend.map((e) => ({ id: e.id, name: e.name }))); }
    else setIdx(idx + 1);
  };
  const handleFinish = (block) => {
    const pend = exercisesNeedingRir(block);
    if (pend.length) { pendingNavRef.current = 'finish'; setRirQueue(pend.map((e) => ({ id: e.id, name: e.name }))); }
    else finish();
  };

  // Edit a set: update state, mark touched/unsaved, and debounce an auto-save.
  const updateSet = (exId, i, field, value) => {
    setSets((prev) => {
      const rows = prev[exId].map((r, ri) => (ri === i ? { ...r, [field]: value, saved: false, touched: true } : r));
      const key = `${exId}-${i}`;
      clearTimeout(saveTimers.current[key]);
      saveTimers.current[key] = setTimeout(() => persistSet(exId, i, rows[i]), 700);
      return { ...prev, [exId]: rows };
    });
  };

  const addSet = (ex) => {
    setSets((prev) => ({
      ...prev,
      [ex.id]: [...prev[ex.id], { weight: prefillWeight(ex, prev[ex.id].length), reps: String(ex.targetReps), rpe: '', saved: false, touched: false }],
    }));
  };

  // Add one round to a superset/circuit: appends a set row to every exercise in it.
  const addRound = (block) => {
    setSets((prev) => {
      const next = { ...prev };
      for (const ex of block.exercises) {
        next[ex.id] = [...(prev[ex.id] || []), { weight: prefillWeight(ex, (prev[ex.id] || []).length), reps: String(ex.targetReps), rpe: '', saved: false, touched: false }];
      }
      return next;
    });
  };

  const finish = async () => {
    // Flush pending debounces; save only the sets the user actually touched
    // (pre-filled-but-untouched sets are the plan, not logged work).
    Object.values(saveTimers.current).forEach(clearTimeout);
    const pending = [];
    for (const ex of workout) {
      (sets[ex.id] || []).forEach((row, i) => {
        if (row.touched && hasData(row)) pending.push(postSet(ex.id, i, row).catch(() => {}));
      });
    }
    await Promise.all(pending);
    const res = await api.post(`/sessions/${sessionId}/complete`, {}, token);
    setSummary(res.totals);
    setNewPRs(res.prs || []);
    setPhase('done');
  };

  // Discard the in-progress session entirely (started the wrong workout, etc.):
  // deletes the session + its logged sets server-side, then exits to Home.
  const discardWorkout = async () => {
    try {
      if (sessionId) await api.delete(`/sessions/${sessionId}`, token);
      onExit();
    } catch {
      setDiscardError('Could not discard the workout. Try again.');
    }
  };

  const saveNote = async (exId) => {
    try {
      await api.put(`/exercises/${exId}/note`, { note: notes[exId] ?? '' }, token);
      setNoteSaved((p) => ({ ...p, [exId]: true }));
    } catch { /* leave unsaved state */ }
  };

  // Substitute one exercise for another (this session only); keep the prescription
  // and, for superset/circuit members, the same number of rounds as its siblings.
  // `subOpen` holds the exercise being replaced (set when the picker was opened).
  const substitute = async (lib) => {
    const orig = subOpen;
    if (!orig) return;
    const rowCount = (sets[orig.id] || []).length || orig.targetSets;
    let last = null, note = '';
    try {
      const td = await api.get(`/exercises/training-data?ids=${lib.id}`, token);
      last = td[lib.id]?.last || null;
      note = td[lib.id]?.note || '';
    } catch { /* ignore */ }
    const computed = suggestWeight(last, orig.targetReps);
    const defWeight = computed != null ? String(computed) : '0';
    const newEx = {
      id: lib.id, name: lib.name, youtube_video_id: lib.youtube_video_id, muscle_group: lib.muscle_group,
      targetSets: orig.targetSets, targetReps: orig.targetReps, targetRepsText: orig.targetRepsText,
      suggested: computed != null ? computed : orig.suggested,
      suggestedText: defWeight ? `${defWeight} lb` : orig.suggestedText,
      suggestedFromHistory: computed != null,
      coach: `Subbed in for ${orig.name}.`, last, substituted: true,
      blockType: orig.blockType, blockId: orig.blockId, blockRounds: orig.blockRounds,
    };
    setWorkout((prev) => prev.map((e) => (e.id === orig.id ? newEx : e)));
    setSets((prev) => ({
      ...prev,
      [lib.id]: Array.from({ length: rowCount }, () => ({ weight: defWeight, reps: String(orig.targetReps), rpe: '', saved: false, touched: false })),
    }));
    setNotes((prev) => ({ ...prev, [lib.id]: note }));
    setSubOpen(null);
  };

  // Add a brand-new exercise to this session (appended to the end). If it's
  // already in the workout, just jump to it (one slot per exercise per session).
  const addExercise = async (lib) => {
    setAddOpen(false);
    // Already in the workout? Jump to the block that holds it (idx is block-based).
    const existingBlock = buildStops(workout).findIndex((g) => g.exercises.some((e) => e.id === lib.id));
    if (existingBlock !== -1) { setIdx(existingBlock); return; }
    let last = null, note = '';
    try {
      const td = await api.get(`/exercises/training-data?ids=${lib.id}`, token);
      last = td[lib.id]?.last || null;
      note = td[lib.id]?.note || '';
    } catch { /* ignore */ }
    const computed = suggestWeight(last, 8);
    const defWeight = computed != null ? String(computed) : '0';
    const newEx = {
      id: lib.id, name: lib.name, youtube_video_id: lib.youtube_video_id, muscle_group: lib.muscle_group,
      targetSets: 3, targetReps: 8, targetRepsText: '8',
      suggested: computed != null ? computed : 0,
      suggestedText: defWeight ? `${defWeight} lb` : '—',
      suggestedFromHistory: computed != null,
      coach: 'Added during the workout.', last, added: true,
      blockType: 'single', blockId: null, blockRounds: null,
    };
    // The added exercise becomes its own single block at the end.
    const newIdx = buildStops(workout).length;
    setWorkout((prev) => [...prev, newEx]);
    setSets((prev) => ({
      ...prev,
      [lib.id]: Array.from({ length: 3 }, () => ({ weight: defWeight, reps: '8', rpe: '', saved: false, touched: false })),
    }));
    setNotes((prev) => ({ ...prev, [lib.id]: note }));
    setIdx(newIdx);
  };

  if (loading) return <p className="text-center text-muted-foreground py-16">Loading…</p>;
  if (!workout || workout.length === 0) {
    return (
      <div className="max-w-md mx-auto text-center py-16 text-muted-foreground px-4">
        {scheduled ? (
          <>
            <Dumbbell className="h-10 w-10 mx-auto mb-3 opacity-40" />
            <p>No exercises in today's {scheduled.focus} workout.</p>
          </>
        ) : (
          <>
            <Coffee className="h-10 w-10 mx-auto mb-3 text-primary" />
            <p className="font-display font-bold text-xl text-foreground mb-1">Rest day</p>
            <p>Nothing scheduled today — recover well. Rest is where the gains are made.</p>
          </>
        )}
      </div>
    );
  }

  // ── Already done today ──────────────────────────────────────────────────
  // A workout already completed for today can't be started again (no doubles).
  // An in-progress session (resumeId) still takes precedence so it can resume.
  if (phase === 'start' && completedToday && !resumeId) {
    const volume = Math.round(Number(completedToday.volume || 0));
    return (
      <div className="max-w-md mx-auto p-4 text-center py-12">
        <Trophy className="h-12 w-12 mx-auto text-primary mb-3" />
        <h2 className="font-display font-bold text-2xl mb-1">Today's workout is done</h2>
        <p className="text-muted-foreground mb-6">
          You crushed {workoutTitle.replace(/^Week \d+ · Day \d+ · /, '')}. Come back tomorrow.
        </p>
        <div className="grid grid-cols-2 gap-3 mb-8 text-left">
          <Stat label="Time" value={fmtDur(completedToday.duration)} />
          <Stat label="Exercises" value={completedToday.exercises ?? 0} />
          <Stat label="Sets" value={completedToday.sets ?? 0} />
          <Stat label="Volume" value={`${volume.toLocaleString()}`} sub="lb" />
        </div>
        <Button className="w-full h-12" onClick={onExit}>Done</Button>
      </div>
    );
  }

  // ── Start screen ──────────────────────────────────────────────────────────
  if (phase === 'start') {
    return (
      <div className="max-w-md mx-auto p-4">
        <div className="flex items-center gap-2 mb-1">
          <img src="/icon-192.png" alt="" className="h-7 w-7 rounded-md" />
          <h2 className="font-display font-bold text-2xl">Today's Workout</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-5">
          {workoutTitle} · {workout.length} exercises{resumeId ? ' · in progress' : ''}
        </p>
        <Button className="w-full h-12 text-base mb-6" onClick={startWorkout}>{resumeId ? 'Resume workout' : 'Start workout'}</Button>

        {warmups.length > 0 && (
          <div className="mb-6">
            <div className="text-sm font-semibold mb-2">Warm-up first (optional)</div>
            <select value={warmupId} onChange={(e) => setWarmupId(e.target.value)}
              className="w-full h-10 rounded-md border border-input bg-background px-2 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              <option value="">No warm-up</option>
              {warmups.map((w) => <option key={w.id} value={w.id}>{w.name} ({w.exercise_count} {w.exercise_count === 1 ? 'move' : 'moves'})</option>)}
            </select>
            {warmupDetail && warmupDetail.exercises.length > 0 && (
              <div className="mt-2 space-y-1.5">
                {warmupDetail.exercises.map((m, i) => (
                  <button key={i} onClick={() => m.youtube_video_id && setWarmupVideo(m.youtube_video_id)}
                    className="w-full flex items-center gap-2.5 rounded-lg border bg-card p-2 text-left disabled:opacity-100" disabled={!m.youtube_video_id}>
                    {m.youtube_video_id
                      ? <img src={`https://i.ytimg.com/vi/${m.youtube_video_id}/default.jpg`} alt="" className="w-14 h-9 object-cover rounded flex-none" />
                      : <div className="w-14 h-9 rounded bg-muted flex items-center justify-center flex-none"><Dumbbell className="h-4 w-4 text-muted-foreground/50" /></div>}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{m.name}</div>
                      <div className="text-xs text-muted-foreground truncate">{m.target_text || ''}{m.coach_note ? ` · ${m.coach_note}` : ''}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="space-y-2 mb-6">
          {groupByBlock(workout).map((g, gi) => (
            <div key={gi} className={`rounded-lg border bg-card p-3 ${g.type !== 'single' ? 'border-primary/40' : ''}`}>
              {g.type !== 'single' && (
                <div className="text-xs font-semibold text-primary uppercase tracking-wide mb-2">
                  {g.type === 'superset' ? 'Superset' : 'Circuit'}{g.rounds ? ` · ${g.rounds} rounds` : ''}
                </div>
              )}
              <div className="space-y-2">
                {g.exercises.map((ex) => (
                  <div key={ex.id} className="flex items-center gap-2.5">
                    {g.type !== 'single' && <span className="text-primary/60 flex-none">•</span>}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{ex.name}</div>
                      <div className="text-xs text-muted-foreground">{ex.targetSets} × {ex.targetRepsText} · {ex.muscle_group}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <button onClick={onExit} className="w-full text-sm text-muted-foreground hover:text-foreground">Cancel</button>

        {warmupVideo && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={() => setWarmupVideo(null)}>
            <div className="w-full max-w-md aspect-video bg-black rounded-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <iframe key={warmupVideo} className="w-full h-full" src={`https://www.youtube.com/embed/${warmupVideo}?rel=0&autoplay=1`} title="Warm-up move"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Summary screen ────────────────────────────────────────────────────────
  if (phase === 'done') {
    const volume = Math.round(Number(summary?.volume || 0));
    return (
      <div className="max-w-md mx-auto p-4 text-center py-12">
        <Trophy className="h-12 w-12 mx-auto text-primary mb-3" />
        <h2 className="font-display font-bold text-2xl mb-1">Workout complete</h2>
        <p className="text-muted-foreground mb-6">Nice work — here's the damage.</p>
        <div className="grid grid-cols-2 gap-3 mb-6">
          <Stat label="Time" value={fmtDur(summary?.duration)} />
          <Stat label="Exercises" value={summary?.exercises ?? 0} />
          <Stat label="Sets" value={summary?.sets ?? 0} />
          <Stat label="Volume" value={`${volume.toLocaleString()}`} sub="lb" />
        </div>

        {newPRs.length > 0 && (
          <div className="rounded-lg border-2 border-primary/50 bg-primary/10 p-3 mb-6 text-left">
            <div className="flex items-center gap-1.5 mb-2 text-primary">
              <Trophy className="h-4 w-4" />
              <span className="text-sm font-bold uppercase tracking-wide">New personal record{newPRs.length > 1 ? 's' : ''}!</span>
            </div>
            <div className="space-y-1">
              {newPRs.map((pr, i) => (
                <div key={i} className="flex justify-between gap-3 text-sm">
                  <span className="font-medium truncate">{pr.name}</span>
                  <span className="text-primary font-semibold flex-none">{pr.label} · {pr.value.toLocaleString()} lb</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {(assessLoading || assessment) && (
          <div className="rounded-lg bg-muted/50 border p-3 mb-6 text-left">
            <div className="flex items-center gap-1.5 mb-1.5 text-primary">
              <img src="/icon-192.png" alt="" className="h-5 w-5 rounded" />
              <span className="text-sm font-semibold">Coach's feedback</span>
            </div>
            {assessLoading
              ? <p className="text-sm text-muted-foreground">Analyzing your workout…</p>
              : <Typewriter text={assessment} />}
          </div>
        )}

        <Button className="w-full h-12" onClick={onExit}>Done</Button>
      </div>
    );
  }

  // ── Player (focus mode) — navigation is by BLOCK ──────────────────────────
  const blocks = buildStops(workout);
  const block = blocks[idx];
  const isMulti = block.exercises.length > 1;        // superset / circuit
  const ex = isMulti ? null : block.exercises[0];    // single-exercise block
  const rows = ex ? sets[ex.id] : null;
  const roundCount = isMulti ? (block.rounds || Math.max(...block.exercises.map((e) => e.targetSets))) : 0;
  const isLast = idx === blocks.length - 1;
  const letter = (i) => String.fromCharCode(65 + i); // A, B, C…

  return (
    <div className="max-w-md mx-auto p-4 pb-28">
      {/* Progress header (block-based) */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium text-muted-foreground">{idx + 1} of {blocks.length}</span>
        <span className="flex items-center gap-1 text-sm font-medium tabular-nums"><Clock className="h-4 w-4 text-primary" />{fmtDur(elapsed)}</span>
        <button onClick={onExit} className="text-muted-foreground hover:text-foreground" aria-label="Exit workout"><X className="h-5 w-5" /></button>
      </div>
      <div className="h-1.5 bg-muted rounded-full mb-4 overflow-hidden">
        <div className="h-full bg-primary transition-all" style={{ width: `${((idx + 1) / blocks.length) * 100}%` }} />
      </div>

      {/* ── Superset / circuit: interleaved, round-by-round ── */}
      {isMulti && (
        <>
          <div className="mb-3">
            <div className="inline-block text-[11px] font-semibold uppercase tracking-wide text-primary bg-primary/10 rounded px-2 py-0.5 mb-1">
              {block.type === 'circuit' ? 'Circuit' : 'Superset'} · {roundCount} {roundCount === 1 ? 'round' : 'rounds'}
            </div>
            <h2 className="font-display font-bold text-2xl leading-tight">{block.type === 'circuit' ? 'Circuit' : 'Superset'}</h2>
            <div className="text-sm text-primary">Do one set of each, then repeat — {block.exercises.length} exercises × {roundCount} rounds</div>
          </div>

          {/* Interleaved set grid — each exercise (with its video thumbnail) per round,
              so the videos stay with their sets instead of one row up top. */}
          <div className="space-y-4">
            {Array.from({ length: roundCount }, (_, r) => (
              <div key={r}>
                <div className="text-xs font-semibold text-muted-foreground mb-1.5 px-1">Round {r + 1}</div>
                <div className="space-y-2">
                  {block.exercises.map((bex, ei) => {
                    const row = (sets[bex.id] || [])[r];
                    if (!row) return null;
                    return (
                      <div key={bex.id} className={`rounded-lg p-2 ${row.saved ? 'bg-primary/5 border border-primary/30' : 'bg-card border'}`}>
                        {/* Header: thumbnail + name (tap to watch) + sub (round 1 only) */}
                        <div className="flex items-center gap-2 mb-2">
                          <button onClick={() => bex.youtube_video_id && setExVideo(bex.youtube_video_id)} disabled={!bex.youtube_video_id}
                            className="flex-1 min-w-0 flex items-center gap-2.5 text-left disabled:opacity-100" aria-label="Watch exercise video">
                            {bex.youtube_video_id
                              ? <img src={`https://i.ytimg.com/vi/${bex.youtube_video_id}/default.jpg`} alt="" className="w-14 h-9 object-cover rounded flex-none" />
                              : <div className="w-14 h-9 rounded bg-muted flex items-center justify-center flex-none"><Dumbbell className="h-4 w-4 text-muted-foreground/50" /></div>}
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium truncate"><span className="text-primary font-bold mr-1">{letter(ei)}</span>{bex.name}{bex.substituted && <span className="font-normal text-muted-foreground"> · sub</span>}</div>
                            </div>
                          </button>
                          {r === 0 && (
                            <button onClick={() => setSubOpen(bex)} aria-label={`Substitute ${bex.name}`}
                              className="flex-none flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground border rounded-md px-1.5 h-7">
                              <Replace className="h-3.5 w-3.5" /> Sub
                            </button>
                          )}
                        </div>
                        {/* Stats + note — shown once per exercise (round 1). */}
                        {r === 0 && (
                          <div className="rounded-md bg-muted/40 border px-2.5 py-2 mb-2 text-xs space-y-1">
                            {bex.last ? (
                              <>
                                <div className="flex justify-between gap-2">
                                  <span className="text-muted-foreground flex-none">Last time</span>
                                  <span className="font-medium text-right">{fmtLast(bex.last)}</span>
                                </div>
                                <div className="flex justify-between gap-2">
                                  <span className="text-muted-foreground">Est. 1RM</span>
                                  <span className="font-medium">{bex.last.e1rm} lb</span>
                                </div>
                              </>
                            ) : (
                              <div className="text-muted-foreground">No previous sets logged yet.</div>
                            )}
                            <div className="flex justify-between gap-2">
                              <span className="text-muted-foreground flex-none">Suggested</span>
                              <span className="font-medium text-primary text-right">{bex.suggestedText}</span>
                            </div>
                            {bex.prs?.e1rm && (
                              <div className="flex justify-between gap-2">
                                <span className="text-muted-foreground flex-none">PR to beat</span>
                                <span className="font-medium text-right flex items-center gap-1 justify-end">
                                  <Trophy className="h-3 w-3 text-primary" />1RM {bex.prs.e1rm.value} lb{bex.prs.max_weight ? ` · top ${bex.prs.max_weight.value} lb` : ''}
                                </span>
                              </div>
                            )}
                            {bex.coach && <div className="text-muted-foreground italic">{bex.coach}</div>}
                            <div className="pt-1.5 mt-0.5 border-t">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-muted-foreground">Your note</span>
                                {noteSaved[bex.id] && <span className="text-primary">Saved</span>}
                              </div>
                              <textarea
                                value={notes[bex.id] ?? ''}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setNotes((p) => ({ ...p, [bex.id]: v }));
                                  setNoteSaved((p) => ({ ...p, [bex.id]: false }));
                                }}
                                onBlur={() => saveNote(bex.id)}
                                rows={2}
                                placeholder="e.g. felt easy — go up 5 lb next time"
                                className="w-full rounded-md border border-input bg-background p-2 text-xs resize-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                              />
                            </div>
                          </div>
                        )}
                        {/* Column labels + full-width set row */}
                        <div className="grid grid-cols-[1fr_1fr_auto] gap-2 text-xs text-muted-foreground mb-1 px-0.5">
                          <span>Weight</span><span>Reps</span><span></span>
                        </div>
                        <div className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
                          <NumInput value={row.weight} onChange={(v) => updateSet(bex.id, r, 'weight', v)} onBlur={() => persistSet(bex.id, r, row)} inputMode="decimal" />
                          <NumInput value={row.reps} onChange={(v) => updateSet(bex.id, r, 'reps', v)} onBlur={() => persistSet(bex.id, r, row)} inputMode="numeric" />
                          <button onClick={() => logSet(bex, r, row)} title={row.saved ? 'Logged' : 'Mark set done'} aria-label="Mark set done"
                            className={`h-10 w-10 rounded-md flex items-center justify-center transition-colors flex-none ${row.saved ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground hover:bg-accent'}`}>
                            <Check className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            <button onClick={() => addRound(block)} className="flex items-center gap-1 text-sm text-primary hover:underline px-1 pt-1">
              <Plus className="h-4 w-4" /> Add round
            </button>
            <p className="text-xs text-muted-foreground px-1 pt-1">Alternate A→B by round — tap a thumbnail to watch. Wheels start at the prescribed numbers; scroll to adjust, then ✓ to log.</p>
          </div>
        </>
      )}

      {/* ── Single exercise ── */}
      {!isMulti && (<>
      {/* Exercise title + substitute */}
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="min-w-0">
          {ex.blockType && ex.blockType !== 'single' && (
            <div className="inline-block text-[11px] font-semibold uppercase tracking-wide text-primary bg-primary/10 rounded px-2 py-0.5 mb-1">
              {ex.blockType === 'superset' ? 'Superset' : 'Circuit'}{ex.blockRounds ? ` · ${ex.blockRounds} rounds` : ''}
            </div>
          )}
          <h2 className="font-display font-bold text-2xl leading-tight">
            {ex.name}{ex.substituted && <span className="text-sm font-normal text-muted-foreground"> · sub</span>}
          </h2>
          <div className="text-sm text-primary">{ex.muscle_group} · target {ex.targetSets} × {ex.targetRepsText}</div>
        </div>
        <button onClick={() => setSubOpen(ex)} className="flex-none flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground border rounded-md px-2 h-8">
          <Replace className="h-4 w-4" /> Sub
        </button>
      </div>

      {/* Video */}
      <div className="aspect-video bg-black rounded-lg overflow-hidden mb-3">
        {ex.youtube_video_id ? (
          <iframe key={ex.id} className="w-full h-full" src={`https://www.youtube.com/embed/${ex.youtube_video_id}?rel=0`} title={ex.name}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground/50 text-sm">No video yet</div>
        )}
      </div>

      {/* Smart panel */}
      <div className="rounded-lg bg-muted/50 border p-3 mb-4 text-sm space-y-1.5">
        {ex.last ? (
          <>
            <div className="flex justify-between gap-3">
              <span className="text-muted-foreground flex-none">Last time</span>
              <span className="font-medium text-right">{fmtLast(ex.last)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Est. 1RM</span>
              <span className="font-medium">{ex.last.e1rm} lb</span>
            </div>
          </>
        ) : (
          <div className="text-muted-foreground">No previous sets logged yet.</div>
        )}
        <div className="flex justify-between gap-3">
          <span className="text-muted-foreground flex-none">Suggested</span>
          <span className="font-medium text-primary text-right">
            {ex.suggestedText}
            {ex.suggestedFromHistory && ex.suggestedDelta !== 0 && (
              <span className="text-xs text-muted-foreground"> ({ex.suggestedDelta > 0 ? '+' : ''}{ex.suggestedDelta} vs last)</span>
            )}
          </span>
        </div>
        {ex.suggestedFromHistory && <div className="text-xs text-muted-foreground">Based on your last session</div>}
        {ex.prs?.e1rm && (
          <div className="flex justify-between gap-3">
            <span className="text-muted-foreground flex-none">PR to beat</span>
            <span className="font-medium text-right flex items-center gap-1 justify-end">
              <Trophy className="h-3.5 w-3.5 text-primary" />1RM {ex.prs.e1rm.value} lb{ex.prs.max_weight ? ` · top ${ex.prs.max_weight.value} lb` : ''}
            </span>
          </div>
        )}
        {ex.coach && <div className="text-muted-foreground italic">{ex.coach}</div>}

        {/* Editable note */}
        <div className="pt-2 mt-1 border-t">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-muted-foreground">Your note</span>
            {noteSaved[ex.id] && <span className="text-xs text-primary">Saved</span>}
          </div>
          <textarea
            value={notes[ex.id] ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              setNotes((p) => ({ ...p, [ex.id]: v }));
              setNoteSaved((p) => ({ ...p, [ex.id]: false }));
            }}
            onBlur={() => saveNote(ex.id)}
            rows={2}
            placeholder="e.g. felt easy — go up 5 lb next time"
            className="w-full rounded-md border border-input bg-background p-2 text-sm resize-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>
      </div>

      {/* Sets */}
      <div className="space-y-2">
        <div className="grid grid-cols-[2rem_1fr_1fr_auto] gap-2 items-center text-xs text-muted-foreground px-1">
          <span>Set</span><span>Weight</span><span>Reps</span><span></span>
        </div>
        {rows.map((row, i) => (
          <div key={i} className={`grid grid-cols-[2rem_1fr_1fr_auto] gap-2 items-center rounded-lg p-1.5 ${row.saved ? 'bg-primary/5 border border-primary/30' : 'bg-card border'}`}>
            <span className="text-center font-semibold text-muted-foreground">{i + 1}</span>
            <NumInput value={row.weight} onChange={(v) => updateSet(ex.id, i, 'weight', v)} onBlur={() => persistSet(ex.id, i, row)} inputMode="decimal" />
            <NumInput value={row.reps} onChange={(v) => updateSet(ex.id, i, 'reps', v)} onBlur={() => persistSet(ex.id, i, row)} inputMode="numeric" />
            <button
              onClick={() => logSet(ex, i, row)}
              title={row.saved ? 'Logged' : 'Mark set done'}
              aria-label="Mark set done"
              className={`h-10 w-10 rounded-md flex items-center justify-center transition-colors ${row.saved ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground hover:bg-accent'}`}
            >
              <Check className="h-5 w-5" />
            </button>
          </div>
        ))}
        <button onClick={() => addSet(ex)} className="flex items-center gap-1 text-sm text-primary hover:underline px-1 pt-1">
          <Plus className="h-4 w-4" /> Add set
        </button>
        <p className="text-xs text-muted-foreground px-1 pt-1">Weights start at last time's numbers — tap to adjust, then ✓ to log each set.</p>
      </div>
      </>)}

      <button onClick={() => setAddOpen(true)} className="w-full mt-4 flex items-center justify-center gap-1.5 h-10 rounded-md border text-sm text-muted-foreground hover:text-foreground">
        <Plus className="h-4 w-4" /> Add another exercise
      </button>

      <button onClick={() => { setDiscardError(null); setConfirmCancel(true); }} className="w-full mt-2 flex items-center justify-center gap-1.5 h-10 text-sm text-destructive/80 hover:text-destructive">
        <Trash2 className="h-4 w-4" /> Discard workout
      </button>

      {/* Sticky nav */}
      <div className="fixed bottom-0 inset-x-0 border-t bg-card p-3" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 0.75rem)' }}>
        <div className="max-w-md mx-auto flex gap-3">
          <Button variant="outline" className="flex-1 h-12" onClick={() => setIdx(idx - 1)} disabled={idx === 0}>
            <ChevronLeft className="h-5 w-5 mr-1" /> Prev
          </Button>
          {isLast ? (
            <Button className="flex-[2] h-12" onClick={() => handleFinish(block)}>Finish workout</Button>
          ) : (
            <Button className="flex-[2] h-12" onClick={() => handleNext(block)}>
              Next <ChevronRight className="h-5 w-5 ml-1" />
            </Button>
          )}
        </div>
      </div>

      {subOpen && <SubPicker api={api} token={token} onPick={substitute} onClose={() => setSubOpen(null)} defaultGroup={subOpen?.muscle_group} />}
      {addOpen && <SubPicker api={api} token={token} onPick={addExercise} onClose={() => setAddOpen(false)} placeholder="Add an exercise…" />}

      {exVideo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={() => setExVideo(null)}>
          <div className="w-full max-w-md aspect-video bg-black rounded-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <iframe key={exVideo} className="w-full h-full" src={`https://www.youtube.com/embed/${exVideo}?rel=0&autoplay=1`} title="Exercise"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
          </div>
        </div>
      )}

      {rirQueue.length > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={skipRir}>
          <div className="w-full max-w-xs bg-card border rounded-xl p-5 text-center" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-bold text-lg mb-1">{rirQueue[0].name}</h3>
            <p className="text-sm text-muted-foreground mb-4">How many more reps could you have done on that last set?</p>
            {/* Keyed by exercise so the buttons remount each question — otherwise the
                button tapped last keeps browser focus and looks pre-selected. */}
            <div key={rirQueue[0].id} className="grid grid-cols-5 gap-2 mb-2">
              {[0, 1, 2, 3, 4].map((n) => (
                <button key={n} onClick={(e) => { e.currentTarget.blur(); answerRir(n); }}
                  className="h-12 rounded-md border font-bold text-lg hover:bg-accent hover:border-primary">
                  {n === 4 ? '4+' : n}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mb-3">0 = couldn't do another · 4+ = had plenty left</p>
            <button onClick={skipRir} className="text-sm text-muted-foreground hover:text-foreground">Skip</button>
          </div>
        </div>
      )}

      {confirmCancel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setConfirmCancel(false)}>
          <div className="w-full max-w-xs bg-card border rounded-xl p-5 text-center" onClick={(e) => e.stopPropagation()}>
            <Trash2 className="h-8 w-8 mx-auto text-destructive mb-2" />
            <h3 className="font-display font-bold text-lg mb-1">Discard this workout?</h3>
            <p className="text-sm text-muted-foreground mb-4">This deletes the session and any sets you've logged. It can't be undone.</p>
            {discardError && <p className="text-sm text-destructive mb-3">{discardError}</p>}
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setConfirmCancel(false)}>Keep going</Button>
              <Button variant="destructive" className="flex-1" onClick={discardWorkout}>Discard</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SubPicker = ({ api, token, onPick, onClose, placeholder = 'Substitute with…', defaultGroup = 'All' }) => {
  useBackHandler(true, onClose);
  const [q, setQ] = useState('');
  const [group, setGroup] = useState(defaultGroup || 'All');
  const [groups, setGroups] = useState([]);
  const [list, setList] = useState([]);

  useEffect(() => { api.get('/exercises/groups', token).then(setGroups).catch(() => setGroups([])); }, [api, token]);
  useEffect(() => {
    const t = setTimeout(() => {
      const params = [];
      if (group !== 'All') params.push(`group=${encodeURIComponent(group)}`);
      if (q.trim()) params.push(`q=${encodeURIComponent(q.trim())}`);
      api.get(`/exercises${params.length ? `?${params.join('&')}` : ''}`, token)
        .then(setList).catch(() => setList([]));
    }, 200);
    return () => clearTimeout(t);
  }, [api, token, q, group]);

  return (
    <div className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center bg-black/60" onClick={onClose}>
      <div className="w-full sm:max-w-md bg-card sm:border sm:rounded-xl h-full sm:h-auto sm:max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()} style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <div className="p-3 border-b flex-none space-y-2">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder}
                className="w-full h-10 rounded-md border border-input bg-background pl-8 pr-3 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
            </div>
            <button onClick={onClose} aria-label="Close"><X className="h-5 w-5 text-muted-foreground" /></button>
          </div>
          <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
            {['All', ...groups.map((g) => g.muscle_group)].map((g) => (
              <button key={g} onClick={() => setGroup(g)}
                className={`flex-none px-3 h-7 rounded-full text-xs font-medium border ${group === g ? 'bg-primary text-primary-foreground border-primary' : 'bg-background text-muted-foreground border-input'}`}>
                {g}
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-y-auto p-2 space-y-1">
          {list.map((ex) => (
            <button key={ex.id} onClick={() => onPick(ex)} className="w-full text-left flex items-center gap-3 rounded-md p-2 hover:bg-accent">
              {ex.youtube_video_id ? (
                <img src={`https://i.ytimg.com/vi/${ex.youtube_video_id}/default.jpg`} alt="" className="w-14 h-9 object-cover rounded flex-none" />
              ) : (
                <div className="w-14 h-9 rounded bg-muted flex items-center justify-center flex-none"><Dumbbell className="h-4 w-4 text-muted-foreground/50" /></div>
              )}
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{ex.name}</div>
                <div className="text-xs text-primary">{ex.muscle_group}</div>
              </div>
            </button>
          ))}
          {list.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">No exercises found.</p>}
        </div>
      </div>
    </div>
  );
};

// Numeric entry — text input with inputMode so phones pop the numpad (the old
// scroll-wheel <select> wasn't practical in the gym). decimal for weight, numeric
// for reps/RPE.
const NumInput = ({ value, onChange, onBlur, inputMode = 'numeric', placeholder }) => (
  <input
    type="text"
    inputMode={inputMode}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    onFocus={(e) => e.target.select()}
    onBlur={onBlur}
    placeholder={placeholder}
    className={`h-10 w-full rounded-md border border-input bg-background text-center text-lg font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${value === '' ? 'text-muted-foreground/50' : ''}`}
  />
);

const Stat = ({ label, value, sub }) => (
  <div className="rounded-lg border bg-card p-3">
    <div className="text-2xl font-bold">{value}<span className="text-sm font-normal text-muted-foreground">{sub ? ` ${sub}` : ''}</span></div>
    <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
  </div>
);

// Types text out character-by-character — makes the coach feedback feel alive
// so users actually read it instead of glossing over a static block.
const Typewriter = ({ text, speed = 16 }) => {
  const [n, setN] = useState(0);
  useEffect(() => { setN(0); }, [text]);
  useEffect(() => {
    if (!text || n >= text.length) return undefined;
    const t = setTimeout(() => setN((x) => x + 1), speed);
    return () => clearTimeout(t);
  }, [n, text, speed]);
  return (
    <p className="text-sm leading-relaxed">
      {text ? text.slice(0, n) : ''}
      {text && n < text.length && <span className="text-primary animate-pulse">▋</span>}
    </p>
  );
};

export default WorkoutView;
